import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Swal from "sweetalert2";
import { updateGHSingleService } from "../../../services/guestHouseServicesApi";

const ITEM_STATUSES = ["Pending", "In Progress", "Completed", "Cancelled"];

export default function EditServiceItemModal({
  open,
  onClose,
  record,
  serviceItem,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    price: "",
    quantity: 1,
    discount_percentage: 0,
    status: "In Progress",
    notes: "",
  });

  useEffect(() => {
    if (serviceItem) {
      setFormData({
        price: serviceItem.price ?? "",
        quantity: serviceItem.quantity ?? 1,
        discount_percentage: serviceItem.discount_percentage ?? 0,
        status: serviceItem.status || "In Progress",
        notes: serviceItem.notes || serviceItem.description || "",
      });
    }
  }, [serviceItem]);

  if (!open || !record || !serviceItem) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const lineBase = (Number(formData.price) || 0) * (Number(formData.quantity) || 1);
  const lineDiscount = ((Number(formData.discount_percentage) || 0) * lineBase) / 100;
  const lineTotal = Math.max(0, lineBase - lineDiscount);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateGHSingleService(
        record._id || record.guestHouseServiceId,
        serviceItem._id || serviceItem.serviceId,
        {
          price: Number(formData.price),
          quantity: Number(formData.quantity),
          discount_percentage: Number(formData.discount_percentage) || 0,
          status: formData.status,
          notes: formData.notes,
        }
      );

      Swal.fire({
        title: "Service Updated!",
        text: `Service "${serviceItem.serviceName}" has been updated.`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Error updating service item:", err);
      Swal.fire("Error", typeof err === "string" ? err : "Failed to update service", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="gh-modal-dialog"
      scroll="paper"
      disableAutoFocus
      disableRestoreFocus
      PaperProps={{
        style: {
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          maxHeight: "calc(100vh - 48px)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
        },
      }}
    >
      <div
        className="gh-modal-header"
        style={{
          background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
          padding: "16px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#fff",
          flexShrink: 0,
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <i className="fa fa-pencil-square-o fs-5" />
          <div>
            <h5 className="mb-0 fw-bold" style={{ fontSize: "16px" }}>
              Edit Service Item
            </h5>
            <small style={{ color: "rgba(255,255,255,0.85)" }}>
              {serviceItem.serviceName} ({serviceItem.serviceId || "Item"})
            </small>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            color: "#fff",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            cursor: "pointer",
            transition: "background 0.2s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
        >
          <i className="fa fa-times" />
        </button>
      </div>

      <DialogContent style={{ padding: "20px", flex: "1 1 auto", overflowY: "auto" }}>
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small fw-bold text-dark">
                Unit Price (₹) <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="0"
                required
                name="price"
                value={formData.price}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label small fw-bold text-dark">
                Quantity <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="1"
                required
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label small fw-bold text-dark">Discount %</label>
              <input
                type="number"
                min="0"
                max="100"
                name="discount_percentage"
                value={formData.discount_percentage}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label small fw-bold text-dark">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-select"
              >
                {ITEM_STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label small fw-bold text-muted">Computed Total</label>
              <div className="form-control bg-light fw-bold text-success">
                ₹{lineTotal.toLocaleString()}
              </div>
            </div>

            <div className="col-12">
              <label className="form-label small fw-bold text-dark">Delivery / Item Remarks</label>
              <textarea
                rows="2"
                name="notes"
                placeholder="e.g. Delivered on 22nd, special request fulfilled"
                value={formData.notes}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-light btn-sm px-3"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm px-4 fw-bold"
              disabled={loading}
              style={{ backgroundColor: "#4f46e5", borderColor: "#4f46e5" }}
            >
              {loading ? <i className="fa fa-spinner fa-spin" /> : "Save Changes"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
